import { clsx } from "clsx";
import { ChevronDown, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";

import { Badge } from "./ui/badge";

export interface TierOption {
  id: string;
  name: string;
  messages: number;
  monthlyPrice: number;
  annualPrice: number;
  multiplier?: string;
  persona: string;
}

interface TierSelectorProps {
  tiers: TierOption[];
  selectedTier: TierOption;
  onSelectTier: (tier: TierOption) => void;
}

export function TierSelector({
  tiers,
  selectedTier,
  onSelectTier,
}: TierSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#0247FE]/20 bg-gradient-to-r from-[#0247FE]/5 to-[#06B6D4]/5 px-3 py-1.5 transition-all hover:from-[#0247FE]/10 hover:to-[#06B6D4]/10 focus:ring-2 focus:ring-[#0247FE]/50 focus:ring-offset-1 focus:outline-none"
        aria-label="Select pricing tier"
        aria-expanded={isOpen}
      >
        <span className="text-sm text-gray-900" style={{ fontWeight: 600 }}>
          {selectedTier.messages} messages
        </span>
        <ChevronDown
          className={clsx(
            "h-3.5 w-3.5 text-gray-600 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div className="animate-in fade-in zoom-in-95 absolute top-full right-0 z-10 mt-2 min-w-[200px] overflow-hidden rounded-lg border bg-white shadow-xl duration-200">
          {tiers.map((tier) => (
            <button
              key={tier.id}
              onClick={() => {
                onSelectTier(tier);
                setIsOpen(false);
              }}
              className={clsx(
                "flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gradient-to-r hover:from-[#0247FE]/5 hover:to-[#06B6D4]/5",
                selectedTier.id === tier.id &&
                  "bg-gradient-to-r from-[#0247FE]/5 to-[#06B6D4]/5",
              )}
            >
              <div className="flex flex-1 items-center gap-2">
                <span
                  className="text-sm text-gray-900"
                  style={{ fontWeight: 600 }}
                >
                  {tier.messages} messages
                </span>
                {tier.multiplier && (
                  <Badge className="border-0 bg-gradient-to-r from-[#0247FE] to-[#06B6D4] px-1.5 py-0 text-xs text-white">
                    {tier.multiplier}
                  </Badge>
                )}
              </div>
              {selectedTier.id === tier.id && (
                <Check className="h-4 w-4 shrink-0 text-[#0247FE]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
